import { readStored, writeStored } from '@claritio/frontend/lib/storage'
// ENG-111: language lives in the URL path (/en, /pl) as the single source of
// truth, so a refresh keeps it. The router mounts under basename=`/${locale}`
// (App.tsx), which means every existing absolute link/navigate resolves under
// the locale prefix automatically — no per-link rewrites needed.
export const SUPPORTED_LOCALES = ['en', 'pl'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: Locale = 'en'

export function isLocale(x: string | undefined): x is Locale {
  return !!x && (SUPPORTED_LOCALES as readonly string[]).includes(x)
}

// First path segment iff it's a supported locale, else null.
export function localeFromPath(pathname: string): Locale | null {
  const seg = pathname.split('/')[1]
  return isLocale(seg) ? seg : null
}

// Pathname with the leading `/en|/pl` segment removed (always keeps a leading slash).
export function stripLocale(pathname: string): string {
  const loc = localeFromPath(pathname)
  if (!loc) return pathname || '/'
  const rest = pathname.slice(loc.length + 1)
  return rest.startsWith('/') ? rest : `/${rest}`
}

const LOCALE_KEY = 'locale'

// Persist the active locale as the browser's remembered default.
export function rememberLocale(locale: Locale): void {
  writeStored(LOCALE_KEY, locale)
}

// The locale to bootstrap into when the URL has none.
export function storedLocale(): Locale {
  const stored = readStored(LOCALE_KEY)
  return isLocale(stored ?? undefined) ? (stored as Locale) : DEFAULT_LOCALE
}

// Build the URL for the same page under a different locale prefix, preserving
// query string and hash. Pure (takes a Location-like) so it's unit-testable.
export function localeUrl(
  next: Locale,
  loc: { pathname: string; search: string; hash: string },
): string {
  return `/${next}${stripLocale(loc.pathname)}${loc.search}${loc.hash}`
}

// Switch language: persist the choice and hard-navigate to the same page under
// the new prefix. A full reload re-bootstraps the router basename + i18n cleanly.
export function switchLocale(next: Locale): void {
  rememberLocale(next)
  window.location.assign(localeUrl(next, window.location))
}
